import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClients, Client } from '../../clients/useData';
import { useInvoices, Invoice } from '../../../hooks/useInvoices';
import { useTheme } from '../../../contexts/ThemeContext';

export interface TopClientsProps {
  limit?: number;
  sortBy?: 'revenue' | 'activity';
  embedded?: boolean;
}

const TopClients: React.FC<TopClientsProps> = ({ limit = 5, sortBy = 'revenue', embedded }) => {
  const navigate = useNavigate();
  const { clients, isLoading: clientsLoading } = useClients();
  const { invoices, isLoading: invoicesLoading } = useInvoices();
  const { colors } = useTheme();
  const isLoading = clientsLoading || invoicesLoading;
  const cardClass = embedded ? 'min-w-0 flex-1 flex flex-col min-h-0' : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1 max-h-64 overflow-y-auto';

  // Calculate revenue per client
  const clientRevenue = React.useMemo(() => {
    const revenueMap = new Map<number, number>();
    (invoices || []).forEach((inv: Invoice) => {
      const current = revenueMap.get(inv.clientId) || 0;
      revenueMap.set(inv.clientId, current + (inv.invoiceIncome || inv.invoiceAmount || 0));
    });
    return revenueMap;
  }, [invoices]);

  const topClients = React.useMemo(() => {
    if (!clients) return [];
    
    return (clients || [])
      .map((client: Client) => ({
        ...client,
        revenue: clientRevenue.get(client.clientId) || 0,
        invoiceCount: (invoices || []).filter((inv: Invoice) => inv.clientId === client.clientId).length,
      }))
      .sort((a, b) => {
        if (sortBy === 'revenue') {
          return b.revenue - a.revenue;
        } else {
          return b.invoiceCount - a.invoiceCount;
        }
      })
      .slice(0, limit);
  }, [clients, clientRevenue, invoices, sortBy, limit]);

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>Top Clients</h2>
        <div className={listClass}>
          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} className={`flex items-center space-x-2 sm:space-x-3 pb-2 ${colors.border} border-b last:border-0`}>
              <div className={`h-7 w-7 sm:h-8 sm:w-8 rounded-full ${colors.surfaceHover} animate-pulse flex-shrink-0`}></div>
              <div className="flex-1 min-w-0">
                <div className={`h-3 ${colors.surfaceHover} animate-pulse rounded w-3/4 mb-1 sm:mb-1.5`}></div>
                <div className={`h-2 sm:h-2.5 ${colors.surfaceHover} animate-pulse rounded w-1/2`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>Top Clients</h2>
        <button
          onClick={() => navigate('/clients')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {topClients.length > 0 ? (
          topClients.map((client, index) => (
            <div
              key={client.clientId}
              onClick={() => navigate(`/clients/${client.clientId}`)}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
            >
              <div className="flex-shrink-0">
                <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-teal-50 text-teal-700 ring-1 ring-teal-200/70 dark:bg-teal-900/30 dark:text-teal-300 dark:ring-teal-800/50 flex items-center justify-center text-[10px] sm:text-xs font-semibold">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[10px] sm:text-xs font-medium ${colors.text} truncate`}>{client.clientName || `Client #${client.clientId}`}</p>
                <div className="flex items-center gap-2 sm:gap-3 mt-0.5 flex-wrap">
                  <span className={`text-[10px] sm:text-xs ${colors.textMuted}`}>
                    €{client.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className={`text-[10px] sm:text-xs ${colors.textMuted}`}>
                    {client.invoiceCount} {client.invoiceCount === 1 ? 'invoice' : 'invoices'}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={`text-center py-4 sm:py-6 ${colors.textMuted}`}>
            <svg className={`w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 ${colors.borderLight}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-xs sm:text-sm">No clients found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopClients;

